import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ComicCard from '@/components/feature/ComicCard';
import { categories, getComicsByCategory, getCategoryName } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

export default function Category() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug);
  const [comics, setComics] = useState<Comic[]>([]);
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  useEffect(() => {
    setActiveCategory(slug);
    let result = getComicsByCategory(slug);
    if (sortBy === 'popular') {
      result = [...result].sort((a, b) => {
        const va = parseFloat(a.views.replace(/[万]/g, '')) * (a.views.includes('万') ? 10000 : 1);
        const vb = parseFloat(b.views.replace(/[万]/g, '')) * (b.views.includes('万') ? 10000 : 1);
        return vb - va;
      });
    } else if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    }
    setComics(result);
  }, [slug, sortBy]);

  const categoryName = getCategoryName(activeCategory);

  return (
    <>
      {/* Breadcrumb */}
      <div className="pt-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">首页</Link>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xs"></i>
            </span>
            <span className="text-foreground-800 font-medium">{categoryName}</span>
          </nav>
        </div>
      </div>

      {/* Header */}
      <section className="bg-background-50 pb-6">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
            {categoryName === '全部' ? '全部漫画' : `${categoryName}漫画`}
          </h1>
          <p className="text-sm text-foreground-500 mt-1">
            {categoryName === '全部'
              ? '浏览所有韩漫作品，发现你的下一部心头好'
              : `收录最热门的${categoryName}类韩漫，下拉式阅读，免费在线`
            }
          </p>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="sticky top-16 z-40 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
            {categories.map(cat => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'text-foreground-600 hover:text-primary-600 hover:bg-primary-50'
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${cat.icon} text-sm`}></i>
                </span>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Sort */}
      <section className="bg-background-50 py-4">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-foreground-500">
              共找到 <strong className="text-foreground-800">{comics.length}</strong> 部作品
            </p>
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {([
                { key: 'latest', label: '最新' },
                { key: 'popular', label: '人气' },
                { key: 'rating', label: '评分' },
              ] as const).map(opt => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    sortBy === opt.key
                      ? 'bg-white text-foreground-900'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="bg-background-50 pb-12 md:pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {comics.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 bg-background-100 rounded-full flex items-center justify-center">
                <span className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </span>
              </div>
              <p className="text-foreground-500 text-sm">暂无相关漫画，请尝试其他分类</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop="false">
              {comics.map(comic => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}